/**
 * Omniture.js
 * For tracking events for preference center
 */

import Omniture from '@sfly/uig-omniture';
import { getCurrentEnv } from "@sfly/uig-current-environment-util";
import { getCurrentEnvironmentConfig, logger, useSflyOmnitureDTL } from './AppUtil';
import { getStore } from './ReduxUtil';
import { DEFAULT_BRAND, MIGRATION_STATES, COMPLEX_PASSWORD_MAPPING } from './AppConstant';

// policy: only load DTL event once per page load
const signinFormInitiateLoaded = {};

function isOnlyPasswordFailing(formErrors) {
    let onlyPasswordFailing = true;
    for (const [key] of Object.entries(formErrors)) {
        if (key !== 'password' && key !== 'confirmPassword' && key !== 'oldPassword' && key !== 'newPassword') {
            onlyPasswordFailing = false;
            break;
        }
    }
    return onlyPasswordFailing;
}
export class OmnitureUtil {

 /**
     * Latest method to track recaptha token when generated successfully
     * @param status
     * @param tokenId
     */

    static recaptchaTokenAnalytic(status, tokenId) {
        if (window._satellite && window._satellite.track) {
            try {
                window._satellite.track("signUpTokenVerification", {
                    "event": {
                        "eventDetails": {
                            "tokenStatus": status,
                            "tokenID": tokenId,
                        }, 
                        "eventInfo": {
                            "eventName": "signsUpTokenVerification"
                        }
                    }
                });
            } catch (error) {
                console.log('error in satellite.track method');
            }
        }
    }
    static amaozonAnalytic(ctaPageName, buttonName, componentName, ctaName="Button Click") {
        let payload={
            "event": {
                "eventDetails": {
                    "ctaName":ctaName,
                    "ctaPageName": ctaPageName,
                    "button": {
                        "buttonName": buttonName
                    },
                    "component": {
                        "componentName":componentName
                    }
                    }
                }, 
            }
        OmnitureUtil.trackEvent(payload);

        if (window._satellite && window._satellite.track) {
            try {
                window._satellite.track("Partners_Link_Click", payload);
            } catch (error) {
                console.log('error in satellite.track method');
            }
        }
    }

    static trackOverlay(popupName, moduleName) {
        const payload = {
          event: {
            eventDetails: {
              popUpName: popupName,
              name: moduleName,
            },
          },
        };
        OmnitureUtil.trackEvent(payload);
        if (window._satellite?.track) {
          try {
            window._satellite.track("Popup_UA", payload);
          } catch (error) {
            console.log("error in satellite.track method");
          }
        }
    }


    /**
     * Latest method in the attempt to align this stuff for LT and SFLY DTLs
     * @param dtlPageName
     * @param dtlFormType
     */
    static trackPageLoaded(dtlPageName, dtlFormType, referringSource, guestCheckout) {
        const { partnerId, partnerSubId, theme, wyngPowered} = getStore().getState().app;
        const data = {
            page: {
                pageInfo: {
                    pageName: dtlPageName,
                    pageType: 'signin_up pages',
                    siteSection: 'signin_up pages',
                },
                pageDetails: {
                    formType: dtlFormType,
                }
            }
        };
        if(referringSource) {
            data['page']['referringDetails'] = { referringSource: referringSource, ...(guestCheckout && { referringSourceID: 'gu=1' }) } 
        }
        if (useSflyOmnitureDTL(theme)) {
            // SFLY
            // If we have partnerId and partnerSubId in the reducer, we will pass them to Omniture
            if (partnerId && partnerSubId) {
                data.session = {
                    partner: {
                        id: partnerId,
                        subId: partnerSubId,
                        "wyngPowerId": wyngPowered
                    },
                    ...(guestCheckout && { visitorDetails: { visitorType: "guest" } })
                }
            }
            OmnitureUtil.putOmniture(data, null);
        } else {
            // LT
            window.LT_DTL = Object.assign(OmnitureUtil.getLifetouchDTL(), data);
            window.document.body.dispatchEvent(new CustomEvent('page_change'));
        }
    }

    /**
     * trackSigninSignupFailure()
     * need to call for signin/signup failure tacking
     */
    static trackSigninSignupFailure(payload) {

        const { theme } = getStore().getState().app;

        let formName = '';
        let errorMessage = payload.errorMessage;
        if (payload.displayMode === '/elevatedSignIn') {
            formName = 'elevated_sign_in_form';
        } else if (payload.displayMode === '/signin/viewSignin') {
            formName = 'sign_in_form';
        } else if (payload.displayMode === '/signup/viewSignup') {
            formName = 'sign_up_form';
        } else if (payload.displayMode === '/logout/viewLogout') {
            formName = 'sign_out_form';
        } else if (payload.displayMode === '/changepassword/viewChangePassword') {
            formName = 'change_password_form';
        } else if (payload.displayMode === '/social/callback') {
            formName = 'social_signin_form';
        }

        const data = {
            event: {
                eventInfo: {
                    eventName: 'form error'
                },
                eventDetails: {
                    formName: formName,
                    formError: errorMessage,
                },
            }
        };

        if (useSflyOmnitureDTL(theme)) {
            OmnitureUtil.putOmniture(data, null);
        } else {
            // LT
            window.LT_DTL = Object.assign(OmnitureUtil.getLifetouchDTL(), data);
            window.document.body.dispatchEvent(new CustomEvent('form_error'));
        }
    }

    /**
     * trackFormErrors()
     * form validation error tracking
     */
    static trackFormErrors(formErrors, logDescription) {
        const onlyPasswordFailing = isOnlyPasswordFailing(formErrors);
        const { dtlPageName, brand } = getStore().getState().app;
        const allMessages = [];
        // eslint-disable-next-line array-callback-return
        Object.keys(formErrors).map((key) => {
            const errors = formErrors[key];
            errors.forEach((errorKey) => {
                if (errorKey) {
                    const errorKeyCleanedForReporting = errorKey.replace(/^(errors\.validation\.form)/, '').replace(/(\.)/g, ' ').trim();
                    // allMessages.push(logDescription);
                    let appendString
                    if(logDescription) 
                        appendString = logDescription + errorKeyCleanedForReporting
                    allMessages.push(appendString ?? errorKeyCleanedForReporting);
                }
            })
        });
        const payload = {
            event: {
                eventInfo: {
                    eventName: 'form error'
                },
                eventDetails: {
                    formName: OmnitureUtil.getFormType(dtlPageName ? dtlPageName : `/sso${window.location.pathname}`, brand),
                    formerror: allMessages.join('|'),
                },
            }
        };

        if (onlyPasswordFailing) {
            payload.event.eventDetails.passwordError = 'Password Unacceptable'
        }
        OmnitureUtil.trackEvent(payload);
    }

    /**
     * trackSigninSignupConfirmation()
     * sign in/sign up/sign out confirmation tracking
     */
    static trackSigninSignupConfirmation(urlPathname, visitorInfo) {
        let type = '';
        if (urlPathname === "/elevatedSignIn") {
            type = "elevated sign in";
        } else if (urlPathname === '/signin/viewSignin') {
            type = "sign in";
        } else if (urlPathname === '/signup/viewSignup') {
            type = "sign up";
        } else if (urlPathname === '/logout/viewLogout') {
            type = "sign out";
        } else if (urlPathname === '/changepassword/viewChangePassword') {
            type = "change password";
        } else if (urlPathname === '/social/callback') {
            type = "social sign in"
        }
        const payload = {
            event: {
                eventInfo: { eventName: `${type} confirmation` },
            }
        };
        if (visitorInfo) {
            payload.event.visitorInfo = visitorInfo;
        }

        // If we have partnerId and partnerSubId in the redux store, we will pass them to Omniture
        const { partnerId, partnerSubId, wyngPowered } = getStore().getState().app;
        if (partnerId && partnerSubId) {
            payload.session = {
                partner: {
                    id: partnerId,
                    subId: partnerSubId,
                    "wyngPowerId": wyngPowered
                }
            };
        }
        OmnitureUtil.trackEvent(payload);
    }

    /**
     * trackSigninSignupFormInitiate()
     * This DTL event should be triggered on click within any sign up form field.
     * Load this DTL event once per page load.
     */
    static trackSigninSignupFormInitiate(urlPathname, brand) {
        if (!signinFormInitiateLoaded[urlPathname]) {
            const payload = {
                event: {
                    eventInfo: { eventName: 'form initiate' },
                    eventDetails: {
                        formName: OmnitureUtil.getFormType(urlPathname, brand),
                    },
                }
            };
            OmnitureUtil.trackEvent(payload);
            signinFormInitiateLoaded[urlPathname] = true;
        }
    }

    /**
     * trackEvent()
     * helper routine to load DTL event
     */
    static trackEvent(event) {
        OmnitureUtil.putOmniture(null, event);
    }

    /**
     * get formType (reflect the brand)
     * expected values are: sfly_signup, tp_signup, lt_signup
     */
    static getFormType(urlPathname, brand) {
        let type = '';
        if (urlPathname === '/elevatedSignIn') {
            type = 'elevatedSignin';
        } else if (urlPathname === '/signin/viewSignin' || urlPathname === '/sso/') {
            type = 'sign in';
        } else if (urlPathname === '/signup/viewSignup') {
            type = 'signup';
        } else if (urlPathname === '/changepassword/viewChangePassword' || urlPathname === '/sso/changepassword') {
            type = 'changepassword';
        } else if (urlPathname.indexOf('/migrate') !== -1) {
            type = 'migrate';
        } else if (urlPathname.indexOf('/signup') > -1) {
            type = "sign up";
        }
        else if (urlPathname.indexOf('/resetPassword') > -1) {
            type = "resetpassword";
        } else if (urlPathname === '/sso/forgotPassword') {
            type = 'forgotpassword';
        }
        return `${brand || DEFAULT_BRAND}_${type}`;
    }

    /**
     * format date string according to DTL requirements specs
     */
    static formatDate(date) {
        let dd = date.getDate();
        let mm = date.getMonth() + 1;
        const yyyy = date.getFullYear();
        if (dd < 10) {
            dd = `0${dd}`;
        }
        if (mm < 10) {
            mm = `0${mm}`
        }
        return `${yyyy}-${mm}-${dd}`;
    }

    static isObject(item) {
        return item && (typeof (item) === 'object') && !Array.isArray(item);
    }

    static mergeDeep(target, ...sources) {
        if (!sources.length) {
            return target;
        }
        const source = sources.shift();
        if (OmnitureUtil.isObject(target) && OmnitureUtil.isObject(source)) {
            for (const key in source) {
                if (OmnitureUtil.isObject(source[key])) {
                    if (!target[key]) {
                        const empty = {};
                        empty[key] = {};
                        Object.assign(target, empty);
                    }
                    OmnitureUtil.mergeDeep(target[key], source[key]);
                }
                else {
                    const empty = {}
                    empty[key] = source[key];
                    Object.assign(target, empty);
                }
            }
        }
        return OmnitureUtil.mergeDeep(target, ...sources);
    }

    /**
    * loadSesion()
    * load DTL session
    * needs to called for each event tracking
    */
    static loadSession(callback) {
        window.$omnitureDTL.loadOmnitureSession(
            window.location.origin,
            (resultSession) => {
                try {
                    if (resultSession.session.visitorDetails && resultSession.session.visitorDetails.customerId === undefined) {
                        resultSession.session.visitorDetails.customerId = '';
                    }
                    resultSession.session.visitorDetails.UserAffiliation = ''
                    resultSession.session.noClobber = true;
                    callback(resultSession);
                } catch (err) {
                    logger.error({
                        appContext: {
                            msg: 'Load session error',
                            err
                        }
                    });
                }
            },
        );
    }

    /**
    * putOmniture()
    * check DTL availability, then load session and DTL event
    */
    static putOmniture(pageData, eventData) {

        const { theme } = getStore().getState().app;
        Omniture.setupOmnitureTracking(getCurrentEnvironmentConfig().wwwBaseUrl, null);

        // Only call this for SFLY themes, not Lifetouch themes.
        if (useSflyOmnitureDTL(theme)) {

            // omniture is ready, load session...
            OmnitureUtil.loadSession((session) => {
                const sessionData = {
                    env: session.env ? session.env : getCurrentEnv(),
                    page: {
                        pageInfo: {
                            siteExperience: session.page.pageInfo.siteExperience,
                        }
                    },
                    session: session.session
                }
                const payload = OmnitureUtil.mergeDeep({}, sessionData, pageData, eventData);
                if (eventData) {
                    window.$omnitureDTL.removeOmnitureJSPData(window.location.origin, 'event');
                }
                window.$omnitureDTL.putOmnitureJSPData(window.location.origin, payload);
            });

        }
    }
    static trackProvider(provider) {
        const { theme } = getStore().getState().app;
        if (!useSflyOmnitureDTL(theme)) {
            window.LT_DTL = OmnitureUtil.getLifetouchDTL(provider);
            window.document.body.dispatchEvent(new CustomEvent('login'));
        }
    }

    static trackMismatchError(proivder) {
        const { theme } = getStore().getState().app;
        if (!useSflyOmnitureDTL(theme)) {
            window.LT_DTL = OmnitureUtil.getLifetouchDTL(proivder, true);
            window.document.body.dispatchEvent(new CustomEvent('login'));
        } 
    }

    /**
     * Lifetouch DTL helper method use this to get the common object structure for the LT_DTL
     * @returns {*}
     */
    static getLifetouchDTL(provider = '', mismatchEmail = '') {
        const { cupId } = getStore().getState().app;
        const { userStatus } = getStore().getState().migrate;
        let DTL = window.LT_DTL ? window.LT_DTL : {};
        DTL = Object.assign(DTL, {
            session: {
                partner: {
                    brand: 'Lifetouch',
                    businessLine: OmnitureUtil.getLifetouchBusinessLineForSflyBrand(),
                },
                visitorDetails: {
                    visitorType: cupId ? 'SignedIN' : 'Guest',
                    customerId: null,
                    cognitoUserProfileId: cupId,
                    flowType: userStatus,
                    provider: provider,
                    ...(mismatchEmail && { mismatchEmail })
                }
            }
        });
        return DTL;
    }

    static getLifetouchBusinessLineForSflyBrand() {
        const { brand } = getStore().getState().app;
        switch (brand) {
            case 'prestige':
                return 'SOLO.com';
            case 'preschools':
                return 'Smiles.com';
            case 'mlt':
            default:
        }       return 'MLT.com'
    }

    static getMigratePathNameForCurrentState() {
        const { userStatus, forceEmailVerify } = getStore().getState().migrate;
        const { cupId, profileEmail, email } = getStore().getState().app;

        const path13Candidate = cupId && email !== profileEmail;

        const prefix = '/migrate/';

        // Please note: This is strictly to align marketing names to our code. =(
        switch (userStatus) {
            case MIGRATION_STATES.PRESENT_BOTH:
                if (path13Candidate) {
                    return `${prefix}path13`;
                } else if (cupId) {
                    if (forceEmailVerify) {
                        return `${prefix}path3-A`;
                    }
                    return `${prefix}path3`;
                } else if (forceEmailVerify) {
                    return `${prefix}path5-A`;
                }
                return `${prefix}path5`;
            case MIGRATION_STATES.BRAND_ONLY:
                if (path13Candidate) {
                    return `${prefix}path13`;
                } else if (forceEmailVerify) {
                    return `${prefix}path10-A`;
                }
                return `${prefix}path10`;
            case MIGRATION_STATES.SFLY_ONLY:
                if (path13Candidate) {
                    return `${prefix}path13`;
                } else if (cupId) {
                    return `${prefix}path7`;
                }
                return `${prefix}path8`;
            case MIGRATION_STATES.ABSENT_BOTH:
                if (path13Candidate) {
                    return `${prefix}path13`;
                }
                return `${prefix}path12`;
            case MIGRATION_STATES.PRESENT_MERGED:
                if (path13Candidate) {
                    return `${prefix}path13`;
                }
                return `${prefix}path2`;
            case MIGRATION_STATES.MERGED_AWAIT_CONFIRM:
                if (path13Candidate) {
                    return `${prefix}path13`;
                } else if (cupId) {
                    return `${prefix}path4`;
                }
                return `${prefix}path6`;
            case MIGRATION_STATES.AUTO_MERGED_ERROR:
                // Not sure this is actually a path so giving it path 13.
                return `${prefix}/path13/auto-merge-signed-in`;
            case MIGRATION_STATES.ERROR:
                return `${prefix}error`;
            default:
                return `${prefix}unknown_path`;
        }
    }
    // static loadABTestConfig = () => {
    //     if (window.adobe && window.adobe.target && window.adobe.target.getOffers) {
    //         return window.adobe.target.getOffers({
    //             consumerId: getLocalVisitorId(),
    //             request: {
    //                 experienceCloud: {
    //                     analytics: {
    //                         logging: 'server_side'
    //                     }
    //                 },
    //                 execute: {
    //                     pageLoad: {
    //                         parameters: {
    //                             pageName: '/signup/viewSignup',
    //                             pageType: 'signin_up pages',
    //                             siteSection: 'signin_up pages'
    //                         }
    //                     }
    //                 }
    //             }, timeout: 6000
    //         }).then((offers) => {
    //             if (Object.hasOwnProperty.call(offers.execute.pageLoad, 'options')) {
    //                 const data = offers.execute.pageLoad.options;
    //                 return data;
    //             } else {
    //                 return {};
    //             }
    //         });
    //     } else {
    //         return new Promise((resolve, reject) => {
    //             reject('not found');
    //         })
    //     }
    // }

    static putComplexPasswordSuccessEvent(page) {
        const { passwordStrength } = getStore().getState().form;
        const payload = {
            event: {
                eventDetails: {
                    formName: page + ' form',
                    passwordStrength: COMPLEX_PASSWORD_MAPPING[passwordStrength]
                },
                eventInfo: {
                    eventName: "formSuccess"
                }
            }

        }
        if (window._satellite && window._satellite.track) {
            try {
                window._satellite.track("passwordSuccess", payload);
            } catch (error) {
                console.log('error in satellite.track method');
            }
        }
    }

    static fbSignupSuccess() {
        const payload = {
            event: {
                eventDetails: {
                    eventSignUp: "fb sign up confirm"
                },
                eventInfo: {
                    eventName: "FBSignUpSuccess"
                }
            }
        }
        if (window._satellite && window._satellite.track) {
            try {
                window._satellite.track("FBSignUpSuccess", payload);
            } catch (error) {
                console.log('error in satellite.track method');
            }
        }
    }

    static fbSignInSuccess() {
        const payload = {
            event: {
                eventDetails: {
                    eventLogin: "fb sign in confirm"
                },
                eventInfo: {
                    eventName: "FBSignInSuccess"
                }
            }
        }
        if (window._satellite && window._satellite.track) {
            try {
                window._satellite.track("FBSignInSuccess", payload);
            } catch (error) {
                console.log('error in satellite.track method');
            }
        }

    }
    static FBElevateSuccess() {
        const payload = {
            event: {
                eventDetails: {
                    eventElevate: "fb sign in confirm - elevation"
                },
                eventInfo: {
                    eventName: "FBElevateSuccess"
                }
            }
        }
        if (window._satellite && window._satellite.track) {
            try {
                window._satellite.track("FBElevateSuccess", payload);
            } catch (error) {
                console.log('error in satellite.track method');
            }
        }
    }


    static fbFailure(error) {
        const payload = {
            event: {
                eventDetails: {
                    errorName: error,
                    errorLogin: "fb email declined",
                    errorType: 404
                },
                eventInfo: {
                    eventName: "FBFailed"
                }
            }
        }
        if (window._satellite && window._satellite.track) {
            try {
                window._satellite.track("FBFailed", payload);
            } catch (error) {
                console.log('error in satellite.track method');
            }
        }
    }

    static trackSocialOmnitureEvent = (eventName, formName, formError) => {
        const data = {
            event: {
                eventInfo: {
                    eventName: eventName,
                    siteSection: "signin_up pages"
                },
                eventDetails: {
                    formName: formName
                }
            }
        };

        if (formError) {
            data.event.eventDetails.formError = formError;
        }

        OmnitureUtil.putOmniture(data, null);
    }

    static trackVisitorEvent = ()=>  {
        const { theme } = getStore().getState().app;
        // Only call this for SFLY themes, not Lifetouch themes.
        if (useSflyOmnitureDTL(theme)) {
            // omniture is ready, load session...
            OmnitureUtil.loadSession((session) => {
                const sessionData = {
                    env: session.env ? session.env : getCurrentEnv(),
                    page: {
                        pageInfo: {
                            siteExperience: session.page.pageInfo.siteExperience,
                        }
                    },
                    session: session.session
                };
                let pageTransactionInfo = JSON.parse(localStorage.getItem('pageTransactionInfo'));
                if(pageTransactionInfo) {
                    delete pageTransactionInfo.txId;
                    delete pageTransactionInfo.page;
                    delete pageTransactionInfo.error;
                    delete pageTransactionInfo.nativeLinking;
                    pageTransactionInfo['newSocialLoginprovider'] = pageTransactionInfo.isLinkingRequired;
                    delete pageTransactionInfo.isLinkingRequired;
                }
                sessionData['session']['visitorDetails'] = {
                    ...session.session.visitorDetails,
                    ...pageTransactionInfo
                }
                window.$omnitureDTL.putOmnitureJSPData(window.location.origin, sessionData);
            });
        }
    }
 }


